import type { TerrainType } from '@fleetduel/shared'

interface TerrainMarkerProps {
  type: TerrainType
}

/**
 * 2D terrain cell markers (ART_GUIDE_v3 §5 — 2D fallback representation).
 * Terrain cells are blocked: no placement, no targeting.
 */
export function TerrainMarker({ type }: TerrainMarkerProps) {
  return (
    <svg
      viewBox="0 0 64 64"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
      aria-label={TERRAIN_LABELS[type]}
      role="img"
    >
      {/* Dim the water beneath so blocked cells read at a glance */}
      <rect x={0} y={0} width={64} height={64} fill="rgba(11, 37, 69, 0.45)" />
      {TERRAIN_SHAPES[type]}
    </svg>
  )
}

export const TERRAIN_LABELS: Record<TerrainType, string> = {
  oil_platform: 'Oil platform (blocked)',
  iceberg: 'Iceberg (blocked)',
  island: 'Island (blocked)',
  reef: 'Reef (blocked)',
}

/** hormuz-platform #5A5046 per ART_GUIDE_v3 palette */
const TERRAIN_SHAPES: Record<TerrainType, React.ReactNode> = {
  oil_platform: (
    <g>
      {/* Legs */}
      <rect x={16} y={36} width={4} height={16} fill="#453D36" />
      <rect x={44} y={36} width={4} height={16} fill="#453D36" />
      {/* Deck */}
      <rect x={10} y={30} width={44} height={8} fill="#5A5046" />
      {/* Derrick tower */}
      <path d="M28 30 L32 12 L36 30 Z" fill="none" stroke="#5A5046" strokeWidth={3} />
      {/* Flare tip */}
      <circle cx={32} cy={10} r={3} fill="#F4B942" />
      {/* Water line shadow */}
      <rect x={12} y={52} width={40} height={3} fill="rgba(0,0,0,0.35)" rx={1.5} />
    </g>
  ),
  iceberg: (
    <g>
      <path d="M12 50 L22 26 L30 36 L38 18 L48 38 L52 50 Z" fill="#E8F1F8" />
      <path d="M30 36 L38 18 L48 38 L44 50 L28 50 Z" fill="#C9DCEA" />
      <rect x={10} y={50} width={44} height={3} fill="rgba(0,0,0,0.3)" rx={1.5} />
    </g>
  ),
  island: (
    <g>
      <ellipse cx={32} cy={46} rx={22} ry={10} fill="#C8B27C" />
      <path d="M18 46 Q32 20 46 46 Z" fill="#3D8C5C" />
      <rect x={12} y={52} width={40} height={3} fill="rgba(0,0,0,0.3)" rx={1.5} />
    </g>
  ),
  reef: (
    <g stroke="#3DDC97" strokeWidth={3} strokeLinecap="round" fill="none">
      <path d="M14 44 Q19 34 24 44" />
      <path d="M28 40 Q33 28 38 40" />
      <path d="M42 46 Q47 36 52 46" />
      <circle cx={22} cy={50} r={2} fill="#3DDC97" stroke="none" />
      <circle cx={40} cy={52} r={2} fill="#3DDC97" stroke="none" />
    </g>
  ),
}
